import { useMemo, useState } from "react";
import { Download, Plus, Search } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import { Checkbox } from "@/marketing/components/ui/checkbox";
import { useDebounce } from "@/marketing/hooks/useDebounce";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { getStageLabel } from "@/marketing/lib/campaignPipeline";
import { getStageToneStyle } from "@/marketing/lib/pipelineTone";
import { formatDate } from "@/marketing/lib/formatters";
import { generateCampaignInfluencerCsv, downloadCsv } from "@/marketing/lib/csvExport";
import { ProductChoicePicker } from "./ProductChoicePicker";
import { buildCampaignProductSelectionState } from "./campaignProductSelection";
import { CreatorScoreBadge } from "../score/CreatorScoreBadge";
import { CreatorScoreCutoffLegend } from "../score/CreatorScoreCutoffLegend";
import { useCreatorScores } from "../score/CreatorScoreProvider";
import { compareCreatorScores, SCORE_REVIEW_LABELS } from "@/marketing/models/creatorScore";
import { CampaignBatches } from "../batches/campaignBatches";

interface CreatorsTabProps {
  campaign: InfluencerCampaign;
  pipelineDefinition: CampaignPipelineDefinition;
  creators: CampaignInfluencer[];
  onRowClick: (creator: CampaignInfluencer, visibleQueue?: CampaignInfluencer[]) => void;
  onAddCreator: () => void;
  onMoveToBatch: (influencerIds: string[], batchId: string | null, batchName: string) => Promise<boolean>;
  onManageBatches: () => void;
}

// "all" and "rejected" are reserved; every other value is a stage key from the pipeline definition.
type StageFilterValue = string;

// "all" and "none" are reserved; every other value is a batch id.
type BatchFilterValue = string;

const NO_BATCH = "none";
const BASE_TABLE_COLUMN_COUNT = 8;
const CHOICE_REWARD_TABLE_COLUMN_COUNT = BASE_TABLE_COLUMN_COUNT + 1;

export function CreatorsTab({
  campaign,
  pipelineDefinition,
  creators,
  onRowClick,
  onAddCreator,
  onMoveToBatch,
  onManageBatches,
}: CreatorsTabProps) {
  const { scores, isError: hasScoreError, isLoading: isScoreLoading } = useCreatorScores();
  const canUseScoreControls = !hasScoreError && !isScoreLoading;
  const [scoreSort, setScoreSort] = useState("desc");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<StageFilterValue>("all");
  const [batchFilter, setBatchFilter] = useState<BatchFilterValue>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creatorBeingAssigned, setCreatorBeingAssigned] =
    useState<CampaignInfluencer | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const batches = useMemo(() => CampaignBatches.list(campaign), [campaign]);
  const batchNameById = useMemo(() => new Map(batches.map((batch) => [batch.batchId, batch.name])), [batches]);
  // A filter on a batch that was deleted would hide everyone, so it falls back to all batches.
  const effectiveBatchFilter = batchFilter === "all" || batchFilter === NO_BATCH || batchNameById.has(batchFilter) ? batchFilter : "all";

  const productSelectionState = buildCampaignProductSelectionState(campaign, []);
  const isChoiceRewardCampaign =
    productSelectionState.isRequired && productSelectionState.poolSkus.length > 0;

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of creators) {
      const key = c.rejected ? "rejected" : c.pipelineStage;
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [creators]);

  const filtered = useMemo(() => {
    const rows = creators.filter((c) => {
      const score = c.userId == null ? undefined : scores[c.userId];
      if (canUseScoreControls) {
        if (scoreFilter === "missing" && score?.finalScore != null) return false;
        if (scoreFilter === "stale" && score?.freshness !== "stale") return false;
        if (Object.prototype.hasOwnProperty.call(SCORE_REVIEW_LABELS, scoreFilter) && score?.reviewStatus !== scoreFilter) return false;
      }
      const matchesStage =
        stageFilter === "all" ||
        (stageFilter === "rejected"
          ? c.rejected
          : !c.rejected && c.pipelineStage === stageFilter);
      if (!matchesStage) return false;
      const batchName = c.batchId ? batchNameById.get(c.batchId) : undefined;
      if (effectiveBatchFilter === NO_BATCH && batchName !== undefined) return false;
      if (effectiveBatchFilter !== "all" && effectiveBatchFilter !== NO_BATCH && c.batchId !== effectiveBatchFilter) return false;
      if (debouncedSearch) {
        const term = debouncedSearch.toLowerCase();
        return (
          c.influencerId.toLowerCase().includes(term) ||
          (c.handle?.toLowerCase().includes(term) ?? false) ||
          (c.platform?.toLowerCase().includes(term) ?? false)
        );
      }
      return true;
    });
    if (!canUseScoreControls || scoreSort === "default") return rows;
    return rows.sort((first, second) => compareCreatorScores(
      first.userId == null ? null : scores[first.userId]?.finalScore,
      second.userId == null ? null : scores[second.userId]?.finalScore,
      scoreSort === "desc",
    ));
  }, [creators, stageFilter, effectiveBatchFilter, batchNameById, debouncedSearch, scoreFilter, scoreSort, scores, canUseScoreControls]);

  const allSelected =
    filtered.length > 0 && filtered.every((c) => selected.has(c.influencerId));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filtered.forEach((c) => next.delete(c.influencerId));
    else filtered.forEach((c) => next.add(c.influencerId));
    setSelected(next);
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const exportCsv = (rows: CampaignInfluencer[]) => {
    if (rows.length === 0) return;
    const csv = generateCampaignInfluencerCsv(
      rows,
      new Map(),
      scores,
      hasScoreError ? "unavailable" : isScoreLoading ? "loading" : "ready",
      pipelineDefinition,
    );
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `campaign-creators-${date}.csv`);
  };
  const selectedRows = filtered.filter((c) => selected.has(c.influencerId));
  const handleExportAll = () => exportCsv(filtered);
  const handleExportSelected = () => exportCsv(selectedRows);
  const handleAssignProducts = (creator: CampaignInfluencer) =>
    setCreatorBeingAssigned(creator);
  const handleMoveToBatch = (batchValue: string) => {
    if (!batchValue) return;
    const batchId = batchValue === NO_BATCH ? null : batchValue;
    const batchName = batchId === null ? "no batch" : batchNameById.get(batchId) ?? batchId;
    const movedIds = selectedRows.map((c) => c.influencerId);
    const movedIdSet = new Set(movedIds);
    // onMoveToBatch never rejects; keep the selection after a failure and on success drop only the moved creators.
    void onMoveToBatch(movedIds, batchId, batchName).then((moved) => {
      if (moved) setSelected((current) => new Set([...current].filter((id) => !movedIdSet.has(id))));
    });
  };
  const handlePickerOpenChange = (open: boolean) => {
    if (!open) setCreatorBeingAssigned(null);
  };

  return (
    <div className="tw-flex tw-flex-col">
      {/* Toolbar */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-border tw-px-8 tw-py-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <div className="tw-relative">
            <Search className="tw-absolute tw-left-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search creators…"
              className="tw-h-9 tw-w-[260px] tw-pl-8"
            />
          </div>
          <FilterChip
            label="All"
            count={creators.length}
            active={stageFilter === "all"}
            onClick={() => setStageFilter("all")}
          />
          {pipelineDefinition.stages.map(({ stage, labels }) => (
            <FilterChip
              key={stage}
              label={labels.admin}
              count={stageCounts[stage] ?? 0}
              active={stageFilter === stage}
              onClick={() => setStageFilter(stage)}
            />
          ))}
          <FilterChip
            label="Rejected"
            count={stageCounts['rejected'] ?? 0}
            active={stageFilter === "rejected"}
            onClick={() => setStageFilter("rejected")}
            tone="rose"
          />
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {selectedRows.length > 0 && (
            <>
              <span className="tw-text-sm tw-text-muted-foreground">
                {selectedRows.length} selected
              </span>
              <select
                aria-label="Move to batch"
                className="tw-h-9 tw-rounded-md tw-border tw-bg-background tw-px-2 tw-text-sm"
                value=""
                onChange={(event) => handleMoveToBatch(event.target.value)}
              >
                <option value="">Move to batch…</option>
                {batches.map((batch) => (
                  <option key={batch.batchId} value={batch.batchId}>
                    {batch.name}
                  </option>
                ))}
                <option value={NO_BATCH}>Remove from batch</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                className="tw-gap-1.5"
                onClick={handleExportSelected}
              >
                <Download className="tw-h-3.5 tw-w-3.5" />
                Export Selected ({selectedRows.length})
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            className="tw-gap-1.5"
            onClick={handleExportAll}
          >
            <Download className="tw-h-3.5 tw-w-3.5" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={onManageBatches}>
            Batches
          </Button>
          <Button size="sm" className="tw-gap-1.5" onClick={onAddCreator}>
            <Plus className="tw-h-3.5 tw-w-3.5" />
            Add creator
          </Button>
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-px-8 tw-py-3 tw-text-sm">
        <label>
          Score order{" "}
          <select className="tw-rounded tw-border tw-bg-background tw-p-2 disabled:tw-opacity-50" value={canUseScoreControls ? scoreSort : "default"} disabled={!canUseScoreControls} onChange={(event) => setScoreSort(event.target.value)}>
            <option value="default">Workflow order</option>
            <option value="desc">Highest first</option>
            <option value="asc">Lowest first</option>
          </select>
        </label>
        <label>
          Review status{" "}
          <select className="tw-rounded tw-border tw-bg-background tw-p-2 disabled:tw-opacity-50" value={canUseScoreControls ? scoreFilter : "all"} disabled={!canUseScoreControls} onChange={(event) => setScoreFilter(event.target.value)}>
            <option value="all">All creators</option>
            {Object.entries(SCORE_REVIEW_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            <option value="missing">No numeric score</option>
            <option value="stale">Stale evidence</option>
          </select>
        </label>
        <label>
          Batch{" "}
          <select aria-label="Batch filter" className="tw-rounded tw-border tw-bg-background tw-p-2" value={effectiveBatchFilter} onChange={(event) => setBatchFilter(event.target.value)}>
            <option value="all">All batches</option>
            {batches.map((batch) => <option key={batch.batchId} value={batch.batchId}>{batch.name}</option>)}
            <option value={NO_BATCH}>No batch</option>
          </select>
        </label>
        <span className="tw-text-muted-foreground">{filtered.length} shown · scores are out of 10</span>
        {hasScoreError && <span role="status" className="tw-text-muted-foreground">Scores unavailable; campaign actions remain available.</span>}
        {!hasScoreError && isScoreLoading && <span role="status" className="tw-text-muted-foreground">Scores loading; campaign actions remain available.</span>}
      </div>
      {/* Table */}
      <div className="tw-px-8 tw-py-4">
        <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-border">
          <table className="tw-w-full tw-text-sm">
            <thead className="tw-bg-muted/50">
              <tr className="tw-text-left tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                <th className="tw-w-9 tw-px-3 tw-py-2.5">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Creator</th>
                <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Creator Score</th>
                <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Platform</th>
                <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Stage</th>
                <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Batch</th>
                {isChoiceRewardCampaign && (
                  <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Products</th>
                )}
                <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Last activity</th>
                <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={isChoiceRewardCampaign ? CHOICE_REWARD_TABLE_COLUMN_COUNT : BASE_TABLE_COLUMN_COUNT}
                    className="tw-px-3 tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground"
                  >
                    {search || stageFilter !== "all" || effectiveBatchFilter !== "all" || (canUseScoreControls && scoreFilter !== "all")
                      ? "No creators match the current filter."
                      : "No creators yet. Click + Add creator to invite."}
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr
                    key={c.influencerId}
                    className={cn(
                      "tw-cursor-pointer tw-border-t tw-border-border hover:tw-bg-muted/30",
                      selected.has(c.influencerId) && "tw-bg-blue-50/40",
                    )}
                    onClick={() => onRowClick(c, filtered)}
                  >
                    <td
                      className="tw-px-3 tw-py-2.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selected.has(c.influencerId)}
                        onCheckedChange={() => toggleOne(c.influencerId)}
                        aria-label={`Select ${c.handle ?? c.influencerId}`}
                      />
                    </td>
                    <td className="tw-px-3 tw-py-2.5">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-[10px] tw-font-semibold tw-text-foreground/70">
                          {(c.handle ?? c.influencerId)
                            .replace(/[^a-zA-Z]/g, "")
                            .slice(0, 2)
                            .toUpperCase() || "??"}
                        </div>
                        <div className="tw-min-w-0">
                          <div className="tw-truncate tw-font-medium">
                            @{c.handle ?? c.influencerId.slice(0, 8)}
                          </div>
                          <div className="tw-truncate tw-text-[11px] tw-text-muted-foreground">
                            {c.influencerId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="tw-px-3 tw-py-2.5">
                      <CreatorScoreBadge creator={c} />
                    </td>
                    <td className="tw-px-3 tw-py-2.5 tw-capitalize tw-text-muted-foreground">
                      {c.platform ?? "—"}
                    </td>
                    <td className="tw-px-3 tw-py-2.5">
                      {c.rejected ? (
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-rose-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-rose-700">
                          <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-rose-600" />
                          Rejected
                        </span>
                      ) : (
                        <span
                          className={cn(
                            "tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold",
                            getStageToneStyle(pipelineDefinition, c.pipelineStage)
                              .pill,
                          )}
                        >
                          <span
                            className={cn(
                              "tw-h-1.5 tw-w-1.5 tw-rounded-full",
                              getStageToneStyle(
                                pipelineDefinition,
                                c.pipelineStage,
                              ).dot,
                            )}
                          />
                          {getStageLabel(pipelineDefinition, c.pipelineStage)}
                        </span>
                      )}
                    </td>
                    <td className="tw-px-3 tw-py-2.5 tw-text-[12px] tw-text-muted-foreground">
                      {(c.batchId && batchNameById.get(c.batchId)) || "—"}
                    </td>
                    {isChoiceRewardCampaign && (
                      <td
                        className="tw-px-3 tw-py-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.selectedSkus?.length ? (
                          <span className="tw-text-[12px] tw-text-muted-foreground">
                            {c.selectedSkus.join(", ")}
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="tw-h-7 tw-text-[11px]"
                            onClick={() => handleAssignProducts(c)}
                          >
                            Assign
                          </Button>
                        )}
                      </td>
                    )}
                    <td className="tw-px-3 tw-py-2.5 tw-text-[12px] tw-text-muted-foreground">
                      {c.lastTransitionAt
                        ? formatDate(c.lastTransitionAt)
                        : "—"}
                    </td>
                    <td className="tw-px-3 tw-py-2.5 tw-text-[12px] tw-text-muted-foreground">
                      {formatDate(c.joinedAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <CreatorScoreCutoffLegend />

      {creatorBeingAssigned && (
        <ProductChoicePicker
          campaignId={campaign.campaignId}
          campaign={campaign}
          creator={creatorBeingAssigned}
          open
          onOpenChange={handlePickerOpenChange}
        />
      )}
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
  tone = "default",
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone?: "default" | "rose";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "tw-inline-flex tw-h-7 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-border tw-bg-background tw-px-2.5 tw-text-[12px] tw-font-medium",
        active && "tw-border-blue-600 tw-bg-blue-600 tw-text-white",
        !active && tone === "rose" && "tw-text-rose-700",
        !active && tone === "default" && "tw-text-foreground hover:tw-bg-muted",
      )}
    >
      {label}
      <span
        className={cn(
          "tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-tabular-nums",
          active ? "tw-bg-white/20 tw-text-white" : "tw-bg-muted tw-text-muted-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}
